import { Component, Input, OnInit } from '@angular/core';
import { ModalController, LoadingController, ActionSheetController, AlertController } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { Modelogeneraldatos } from 'src/app/models/modelogeneraldatos';
import { JavapiService } from 'src/app/services/javapi.service';
import { ImageEditorComponent } from '../image-editor/image-editor.component';
import { MetodosgenService } from 'src/app/services/metodosgen.service';
import { Metodosui } from 'src/app/services/metodosui';
import { MeasureCalculatorComponent } from '../measure-calculator/measure-calculator.component';
import { CameraSource } from '@capacitor/camera';
import { InspecdatosareaestimadoComponent } from '../inspecdatosareaestimado/inspecdatosareaestimado.component';

@Component({
  selector: 'app-inspecdatosarea',
  templateUrl: './inspecdatosarea.component.html',
  styleUrls: ['./inspecdatosarea.component.scss'],
  standalone: false
})
export class InspecdatosareaComponent implements OnInit {

  @Input() area: any;
  @Input() inspeccion: any;

  datos: any[] = [];
  loading: boolean = false;
  bgLoading: HTMLIonLoadingElement | null = null;
  segment: string = 'medida';

  constructor(
    private modalCtrl: ModalController,
    private loadingCtrl: LoadingController,
    private actionSheetCtrl: ActionSheetController,
    private alertCtrl: AlertController,
    private javapiService: JavapiService,
    private metodosGenService: MetodosgenService,
    private metodosUi: Metodosui
  ) { }

  ngOnInit() {
    this.loadDatos();
  }

  get filteredDatos() {
    return this.datos.filter(d => d.type === this.segment);
  }

  dismiss() {
    this.modalCtrl.dismiss();
  }

  async showLoading(msg: string) {
    this.bgLoading = await this.loadingCtrl.create({
      message: msg
    });
    await this.bgLoading.present();
  }

  async hideLoading() {
    if (this.bgLoading) {
      await this.bgLoading.dismiss();
      this.bgLoading = null;
    }
  }

  async loadDatos() {
    try {
      this.loading = true;

      const [medidasRes, imgNotasRes, estimadosRes] = await Promise.all([
        firstValueFrom(
          this.javapiService.getCommonModel(
            this.metodosGenService.setModeloGeneral(
              "genModel", "inspeccionMedidas", String(this.area.id), "", "", "", "", "", "", "", "", "", "", "", "", "", ""
            )
          )
        ).catch(() => []),
        firstValueFrom(
          this.javapiService.getCommonModel(
            this.metodosGenService.setModeloGeneral(
              "genModel", "inspeccionImagenesNotas", String(this.area.id), "", "", "", "", "", "", "", "", "", "", "", "", "", ""
            )
          )
        ).catch(() => []),
        firstValueFrom(this.javapiService.getInspeccionEstimadosList(String(this.area.id))).catch(() => [])
      ]);

      const medidas = (medidasRes || []).map(m => ({
        id: m.dt1,
        descripcion: m.dt2,
        ancho: Number(m.dt3) || 0,
        alto: Number(m.dt4) || 0,
        unidad: m.dt5,
        area: Number(m.dt6) || 0,
        foto: m.dt7,
        cantidad: Number(m.dt8) || 1,
        tipo_medida: m.dt9 || 'cuadrada',
        comentario: m.dt11 || '',
        type: 'medida'
      }));

      const imgsNotas = (imgNotasRes || []).map(m => {
        const tipoDb = m.dt2; // IMAGEN, NOTA
        const type = tipoDb === 'IMAGEN' ? 'foto' : 'nota';
        return {
          id: m.dt1,
          comentario: m.dt3,
          foto: m.dt4,
          created_at: m.dt5,
          type: type,
          descripcion: m.dt3,
          ancho: 0, alto: 0, unidad: '', cantidad: 0, tipo_medida: ''
        };
      });

      const estimados = (estimadosRes || []).map(e => ({
        id: e.dt1,
        tipo: e.dt2,
        dias: Number(e.dt3) || 0,
        trabajadores: Number(e.dt4) || 0,
        turno: e.dt5,
        monto_diario: Number(e.dt6) || 0,
        cantidad: Number(e.dt7) || 0,
        costo: Number(e.dt8) || 0,
        subcontratista: e.dt9,
        comentario: e.dt10,
        usuario: e.dt11,
        created_at: e.dt12,
        type: 'nota',
        isEstimado: true
      }));

      this.datos = [...medidas, ...imgsNotas, ...estimados];
      this.loadThumbnails();

    } catch (e) {
      console.error(e);
      this.metodosUi.showToast('Error cargando datos', 'danger');
    } finally {
      this.loading = false;
    }
  }

  async loadThumbnails() {
    for (const item of this.datos) {
      // Solo cargamos miniaturas para ítems de tipo 'foto' que tengan un nombre de archivo válido
      if (item.type === 'foto' && item.foto && item.foto.length > 5 && !item.base64) {
        item.loadingThumb = true;
        try {
          const path = 'path_img_inspecciones';
          this.javapiService.getImage(item.foto, path).subscribe({
            next: (base64: any) => {
              if (base64) {
                item.base64 = base64.toString().startsWith('data:image') 
                  ? base64 
                  : `data:image/jpeg;base64,${base64}`;
              }
              item.loadingThumb = false;
            },
            error: (err) => {
              console.error('Error loading thumb', err);
              item.loadingThumb = false;
            }
          });
        } catch (e) {
          console.error(e);
          item.loadingThumb = false;
        }
      }
    }
  }

  async onFabClick() {
    
    switch (this.segment) {
      case 'medida':
        this.openMedidaModal();
        break;
      case 'foto':
        this.addImagen();
        break;
      case 'nota':
        this.addNota();
        break;
      default:
        this.openMedidaModal();
    }
  }

  async openMedidaModal(medida?: any) {
    const { InspecdatosareamedidasComponent } = await import('../inspecdatosareamedidas/inspecdatosareamedidas.component');

    const props: any = { areaId: this.area.id };
    if (medida) props.medida = medida;

    const modal = await this.modalCtrl.create({
      component: InspecdatosareamedidasComponent,
      componentProps: props
    });

    await modal.present();

    const { data } = await modal.onWillDismiss();
    if (data && data.saved) {
      this.loadDatos();
    }
  }

  async editItem(item: any) {
    if (item.isEstimado) {
      this.openEstimadoModal(item);
    } else if (item.type === 'medida') {
      this.openMedidaModal(item);
    } else if (item.type === 'foto') {
      this.editFotoComment(item);
    } else if (item.type === 'nota') {
      this.editNota(item);
    }
  }

  async editFotoComment(item: any) {
    const alert = await this.alertCtrl.create({
      header: 'Editar Comentario',
      cssClass: 'custom-alert',
      inputs: [
        {
          name: 'comentario',
          type: 'text',
          value: item.comentario,
          placeholder: 'Descripción'
        }
      ],
      buttons: [
        { text: 'Cancelar', role: 'cancel', cssClass: 'alert-button' },
        {
          text: 'Guardar',
          cssClass: 'alert-button',
          handler: (data) => {
            this.saveItems([{
              id: item.id,
              comentario: data.comentario,
              foto: item.foto,
              type: 'foto'
            }], 'foto', 'update');
          }
        }
      ]
    });
    await alert.present();
  }

  async editNota(item: any) {
    const alert = await this.alertCtrl.create({
      header: 'Editar Nota',
      cssClass: 'custom-alert',
      inputs: [
        {
          name: 'nota',
          type: 'textarea',
          value: item.comentario,
          placeholder: 'Escribe tu nota aquí...',
          attributes: { rows: 8 }
        }
      ],
      buttons: [
        { text: 'Cancelar', role: 'cancel', cssClass: 'alert-button' },
        {
          text: 'Guardar',
          cssClass: 'alert-button',
          handler: (data) => {
            if (data.nota) {
              this.saveItems([{
                id: item.id,
                comentario: data.nota,
                foto: '',
                type: 'nota'
              }], 'nota', 'update');
            }
          }
        }
      ]
    });
    await alert.present();
  }

  async addImagen() {
    const actionSheet = await this.actionSheetCtrl.create({
      header: 'Seleccionar Origen',
      cssClass: 'custom-action-sheet',
      buttons: [
        {
          text: 'Cámara',
          icon: 'camera-outline',
          handler: () => {
            this.processImageSelection(CameraSource.Camera);
          }
        },
        {
          text: 'Galería',
          icon: 'images-outline',
          handler: () => {
            this.processImageSelection(CameraSource.Photos);
          }
        },
        {
          text: 'Cancelar',
          icon: 'close-outline',
          role: 'cancel'
        }
      ]
    });
    await actionSheet.present();
  }

  async processImageSelection(source: CameraSource) {
    const image = await this.metodosUi.takePicture(source);

    if (image && image.base64String) {
      const alert = await this.alertCtrl.create({
        header: 'Imagen',
        cssClass: 'custom-alert',
        inputs: [
          {
            name: 'descripcion',
            type: 'textarea',
            placeholder: 'Descripción (opcional)',
            attributes: { rows: 4 }
          }
        ],
        buttons: [
          { text: 'Cancelar', role: 'cancel', cssClass: 'alert-button' },
          {
            text: 'Guardar',
            cssClass: 'alert-button',
            handler: (data) => {
              this.saveItems([{
                comentario: data.descripcion || 'Imagen',
                ancho: 0, alto: 0, unidad: '',
                foto: image.base64String,
                type: 'foto'
              }], 'foto');
            }
          }
        ]
      });
      await alert.present();
    }
  }

  async addNota() {
    this.openEstimadoModal();
  }

  async openEstimadoModal(item?: any) {
    const props: any = { areaId: String(this.area.id) };
    if (item) props.estimado = item;

    const modal = await this.modalCtrl.create({
      component: InspecdatosareaestimadoComponent,
      componentProps: props
    });
    await modal.present();
    const { data } = await modal.onWillDismiss();
    if (data) this.loadDatos();
  }

  async saveItems(items: any[], type: string, subrutina: string = 'insert') {
    await this.showLoading('Guardando...');
    try {
      for (const item of items) {
        if (item.type === 'medida' || type === 'medida') {
          const model = {
            usuario: this.getUsuario(),
            subrutina: subrutina,
            datos: {
              dt1: String(item.id || '0'),
              dt2: String(this.area.id),
              dt3: item.descripcion || '',
              dt4: String(item.ancho || 0),
              dt5: String(item.alto || 0),
              dt6: item.unidad || '',
              dt7: item.foto || '',
              dt8: String(item.cantidad || 1),
              dt9: item.tipo_medida || 'cuadrada',
              dt10: JSON.stringify(item.annotations || []),
              dt11: item.comentario || '',
              dt14: item.originalFoto || ''
            }
          };
          await firstValueFrom(this.javapiService.saveInspeccionMedida(model));
        } else {
          const model = {
            usuario: this.getUsuario(),
            subrutina: subrutina,
            datos: {
              dt1: String(item.id || '0'),
              dt2: String(this.area.id),
              dt3: item.type === 'foto' ? 'IMAGEN' : 'NOTA',
              dt4: item.comentario || '',
              dt5: item.foto || ''
            }
          };
          await firstValueFrom(this.javapiService.saveInspeccionImagenNota(model));
        }
      }
      await this.hideLoading();
      this.metodosUi.showToast('Guardado', 'success');
      this.loadDatos();
    } catch (e) {
      await this.hideLoading();
      console.error(e);
      this.metodosUi.showToast('Error guardando', 'danger');
    }
  }

  async deleteItem(item: any) {
    const confirm = await this.metodosUi.showConfirmation('Confirmar Eliminación', '¿Seguro de eliminar este elemento?', 'Eliminar', true);
    if (!confirm) return;
    try {
      if (item.isEstimado) {
        const payload = {
          subrutina: 'DELETE',
          datos: { dt1: String(item.id) }
        };
        await firstValueFrom(this.javapiService.saveInspeccionEstimado(payload));
      } else if (item.type === 'medida') {
        const model = {
          usuario: this.getUsuario(),
          subrutina: 'delete',
          datos: { dt1: String(item.id), dt2: '0', dt3: '', dt4: '0', dt5: '0', dt6: '', dt7: '' }
        };
        await firstValueFrom(this.javapiService.saveInspeccionMedida(model));
      } else {
        const model = {
          usuario: this.getUsuario(),
          subrutina: 'delete',
          datos: { dt1: String(item.id), dt2: '0', dt3: '', dt4: '', dt5: '' }
        };
        await firstValueFrom(this.javapiService.saveInspeccionImagenNota(model));
      }
      this.metodosUi.showToast('Eliminado', 'success');
      this.loadDatos();
    } catch (e) {
      console.error(e);
      this.metodosUi.showToast('Error eliminando', 'danger');
    }
  }

  async viewImage(item: any) {
    if (!item.foto) return;

    let imagesForSlider: any[] = [];
    let startIndex = 0;

    // Si estamos en el segmento de fotos, permitimos navegar entre todas las fotos de este área
    if (this.segment === 'foto' && item.type === 'foto') {
      const photos = this.datos.filter(d => d.type === 'foto' && d.foto);
      imagesForSlider = photos.map(p => ({
        path: p.foto,
        source: 'inspeccion',
        texto: p.comentario || 'Foto de Inspección'
      }));
      startIndex = photos.findIndex(p => p.id === item.id);
    } else {
      // Para medidas o flujos individuales, mandamos solo esa imagen
      imagesForSlider = [{
        path: item.foto,
        source: item.type === 'medida' ? 'medida' : 'inspeccion',
        texto: item.descripcion || item.comentario || 'Vista Previa'
      }];
      startIndex = 0;
    }

    const { VerimagenComponent } = await import('../verimagen/verimagen.component');
    await this.metodosUi.openModal(VerimagenComponent, {
      images: imagesForSlider,
      startIndex: startIndex >= 0 ? startIndex : 0
    });
  }

  private getUsuario(): string {
    const userJson = localStorage.getItem('user');
    if (userJson) {
      const user = JSON.parse(userJson);
      if (user.l_usuario) return user.l_usuario;
    }
    return 'system';
  }

  trackByFn(index: number, item: any): number {
    return item.id;
  }
}